import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sun, Moon, Translate } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";
import { useTheme } from "@/context/ThemeContext";

/**
 * Shared layout for all static / legal pages (Privacy, Terms, Cookies, FAQ,
 * Support, Contact). Provides a consistent header with logo + back-arrow +
 * language + theme toggles, an editorial reading column, and a footer linking
 * the other policies.
 */
export default function LegalLayout({ kicker, title, children, lastUpdated }) {
  const { t, lang, setLang } = useI18n();
  const { theme, toggle } = useTheme();
  return (
    <div className="min-h-screen tf-bg text-white">
      <header className="tf-glass border-b" style={{ borderColor: "var(--tf-border)" }}>
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="legal-back" className="flex items-center gap-2 hover:opacity-80">
            <div className="w-7 h-7 bg-[#007AFF] flex items-center justify-center">
              <span className="font-display text-sm text-white">TF</span>
            </div>
            <span className="font-display text-lg tracking-tight">TEAMFIT</span>
          </Link>
          <div className="flex items-center gap-1">
            <button
              data-testid="legal-lang-toggle"
              onClick={() => setLang(lang === "da" ? "en" : "da")}
              className="px-2 py-2 hover:bg-white/5 flex items-center gap-1 text-xs uppercase tracking-widest font-bold"
            >
              <Translate weight="duotone" size={16} /> {lang.toUpperCase()}
            </button>
            <button
              data-testid="legal-theme-toggle"
              onClick={toggle}
              className="p-2 hover:bg-white/5"
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun weight="duotone" size={18} /> : <Moon weight="duotone" size={18} />}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
        <Link to="/" data-testid="legal-back-arrow" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-semibold text-zinc-400 hover:text-white mb-8">
          <ArrowLeft weight="bold" size={14} /> {t("legal.back_home")}
        </Link>
        {kicker && (
          <div className="label-overline text-[#007AFF] mb-3">{kicker}</div>
        )}
        <h1 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight mb-6">{title}</h1>
        {lastUpdated && (
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-10 font-mono-tag">
            {t("legal.last_updated")} · {lastUpdated}
          </div>
        )}

        <article className="legal-prose">
          {children}
        </article>
      </main>

      <footer className="border-t" style={{ borderColor: "var(--tf-border)" }}>
        <div className="max-w-3xl mx-auto px-6 py-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="font-mono-tag text-xs text-zinc-500">TEAMFIT · {new Date().getFullYear()}</div>
          <nav className="flex flex-wrap gap-x-5 gap-y-2 text-xs uppercase tracking-widest font-semibold text-zinc-400">
            <Link data-testid="footer-privacy" to="/privacy" className="hover:text-white">{t("legal.nav_privacy")}</Link>
            <Link data-testid="footer-terms" to="/terms" className="hover:text-white">{t("legal.nav_terms")}</Link>
            <Link data-testid="footer-cookies" to="/cookies" className="hover:text-white">{t("legal.nav_cookies")}</Link>
            <Link data-testid="footer-faq" to="/faq" className="hover:text-white">{t("legal.nav_faq")}</Link>
            <Link data-testid="footer-support" to="/support" className="hover:text-white">{t("legal.nav_support")}</Link>
            <Link data-testid="footer-contact" to="/contact" className="hover:text-white">{t("legal.nav_contact")}</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
